"use client";

import React, { useEffect, useState, useCallback } from "react";
import Link from "next/link";

type Props = {
  children: React.ReactNode;
  healthPath?: string;
  timeoutMs?: number;
};

export default function HealthGate({
  children,
  healthPath,
  timeoutMs = 5000,
}: Props) {
  const [status, setStatus] = useState<"loading" | "up" | "down">("up");

  const base = (process.env.NEXT_PUBLIC_WEB_API_URL || "").replace(/\/$/, "");
  const url = healthPath || (base ? `${base}/health` : "/health");

  const check = useCallback(async () => {
    const controller = new AbortController();
    const id = setTimeout(() => controller.abort(), timeoutMs);

    try {
      const res = await fetch(url, {
        signal: controller.signal,
        cache: "no-store",
      });

      if (res.status === 200 || res.status === 304) {
        setStatus("up");
      } else {
        setStatus("down");
      }
    } catch (err) {
      console.error("Health check failed", err);
      setStatus("down");
    } finally {
      clearTimeout(id);
    }
  }, [url, timeoutMs]);

  // 🔹 Run only once on initial mount
  useEffect(() => {
    check();
  }, [check]);

  if (status === "loading") {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-10 h-10 border-4 border-gray-300 border-t-black rounded-full mx-auto mb-4" />
          <p className="text-gray-700">...</p>
        </div>
      </div>
    );
  }

  if (status === "down") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-6">
        <div className="max-w-xl text-center bg-white p-8 rounded-lg shadow">
          <h1 className="text-2xl font-semibold mb-2">
            Site Under Maintenance
          </h1>
          <p className="text-gray-600 mb-4">
            We are currently performing maintenance. Please check back shortly.
          </p>
          <div className="flex items-center justify-center gap-3">
            <button
              onClick={check}
              className="px-4 py-2 bg-blue-600 text-white rounded"
            >
              Retry
            </button>
            <Link href="/" className="px-4 py-2 border rounded text-sm">
              Home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
